/* ------------------------ Functional imports start ------------------------ */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { PaginatedUseQuery, SmartTableColumnType } from "../utils/types"
import { GenericFilterType, PaginatedQueryParameters } from "../api/hooks/utils/types"
import LogTool from "../logger/logTools"
import { useTranslation } from "react-i18next"
import { availableTablePageSizes, defaultTablePageSize} from '../config/ui'
import { type SortOrder } from 'antd/es/table/interface'
/* ------------------------- Functional imports end ------------------------- */


/* -------------------------- Design imports start -------------------------- */
import Table from 'antd/es/table'
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Grid,
  Skeleton,
  Stack,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  FormControlLabel, Checkbox, Typography
} from "@mui/material";
import Button from "./Button";
import { FilterAltRounded, ShareRounded, WarningAmberRounded } from "@mui/icons-material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TableOptions from './widgets/TableOptions'
import TableFilterDrawer from './TableFilterDrawer'
import InfoField from "./inputs/InfoField";
import InfoPanel from "./layout/InfoPanel";
/* --------------------------- Design imports end --------------------------- */

type Props<TEntity, TFilters> = {
  tableId?: string // ID for the table element (e.g., 'articleTable', 'billTable')
  columnsConfig: {
    allColumns: SmartTableColumnType<TEntity>[]
    initialVisibleColumnIndices?: string[] // the column.key prop of the columns that should be visible when rendering the table for the first time
  }
  onColumnVisibilityChange?: (visibleColumnIndices: string[]) => void
  queryConfig: {
    dataHook: PaginatedUseQuery<TEntity, TFilters>
    initialQueryOptions?: PaginatedQueryParameters<TEntity, TFilters>
  }
  queryState?: {
    options: PaginatedQueryParameters<TEntity, TFilters>
    setOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<TEntity, TFilters>>>
  }
  selection?: {
    state: TEntity[],
    setState: React.Dispatch<React.SetStateAction<TEntity[]>>
    onChange?: (selectedRows: TEntity[]) => void
  }
  onShare?: () => void
  actions?: React.ReactNode
  tableOptions?: Array<{
    label: string
    onClick: React.MouseEventHandler<HTMLLIElement>
    id?: string
  }>
  onRow?: {
    onClick?: (record: TEntity, event: React.MouseEvent<any, MouseEvent>) => void
    onDoubleClick?: (event: React.MouseEvent<any, MouseEvent>) => void
  }
  rowClassName?: (record: TEntity, index: number) => string
  contextMenuOptions?: Array<{
    label: string
    icon?: React.ReactNode
    onClick: (record: TEntity, event: React.MouseEvent<any, MouseEvent>) => void
  }>
  customInfoPanel?: React.ReactNode | (
    (open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>) => React.ReactNode
  )
  sx?: React.CSSProperties
  searchLabel?: string
  expandable?: {
    expandedRowRender: (record: TEntity) => React.ReactNode
    rowExpandable?: (record: TEntity) => boolean
  }
  hideFilters?: boolean
  hideColumnSelector?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * A smart table component that takes care of fetching data, rendering loading
 * states, enabling sorting, filtering and pagination, while being optimized for
 * large data sets and partial data updates.
 *
 * The Table component is built upon the Ant Design table component and is powered
 * by the TanStack Query library.
 *
 * How to use:
 *  Place the SmartTable component anywhere and provide it with at least the
 *  required props: columnsConfig, queryConfig.
 *  Using the columnsConfig, SmartTable is able to render an empty table (in its
 *  loading state), enable sorting by all columns that enable sorting, as well as
 *  providing filter input fields and a column selector.
 *  The queryConfig prop contains all information needed to automatically fetch
 *  the required data through the provided smartQuery data hook. It enables pagination,
 *  backend-side sorting and filtering.
 *  The columnsConfig prop contains all information needed to render the table
 *  with its columns and sorting capabilities. A well as the blueprint to render
 *  the filter drawer inputs.
 *
 *
 * @param props
 * @constructor
 */
export default function SmartTable<
  TEntity extends {key: string},
  TFilters extends GenericFilterType
> (props: Props<TEntity, TFilters>): React.ReactNode {
  /* ------------------------- Non state data start ------------------------- */
  const {
    tableId,
    columnsConfig,
    onColumnVisibilityChange,
    selection,
    queryConfig,
    queryState,
    onShare,
    actions = [],
    onRow,
    rowClassName,
    tableOptions,
    contextMenuOptions,
    customInfoPanel,
    sx = {},
    searchLabel,
    expandable,
    hideFilters = false,
    hideColumnSelector = false,
  } = props

  const { t } = useTranslation()
  const log = new LogTool({context: 'SmartTable', enable: true, logLevel: 'debug'})
  const placeholderDataTTD = 200 // time in ms before the table switches from displaying
                                 // placeholder data to displaying the loading state
  const infoPanelDrawerWidth = 350
  const columnVisibilityStorageKey = tableId
    ? `smartTable:${tableId}:visibleColumns`
    : undefined
  /* -------------------------- Non state data end -------------------------- */


  /* --------------------------- Flag states start -------------------------- */
  // table states
  const [showActiveSelectionWarning, setShowActiveSelectionWarning] = useState(false)

  // query states
  const [isPendingForTooLong, setIsPendingForTooLong] = useState(false)

  // info panel states
  const [openInfoPanel, setOpenInfoPanel] = useState(false)
  // table scroll states
  const [tableScrollY, setTableScrollY] = useState<number>(0)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)

  // filter states
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)

  // context menu states
  const [openMoreOptionsMenu, setOpenMoreOptionsMenu] = useState(false)
  /* ---------------------------- Flag states end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  // query states
  const [internalQueryOptions, setInternalQueryOptions] = useState<
    PaginatedQueryParameters<TEntity, TFilters>
  >({
    ...queryConfig.initialQueryOptions,
    // add defaults for page and pageSize if they are not set in the initial query options
    ...(!queryConfig.initialQueryOptions?.page && {page: 1}),
    ...(!queryConfig.initialQueryOptions?.pageSize && {pageSize: defaultTablePageSize}),
  })
  const managedQueryOptions = queryState?.options ?? internalQueryOptions
  const setManagedQueryOptions = queryState?.setOptions ?? setInternalQueryOptions
  const {
    data,
    isPlaceholderData,
    error,
    isPending,
    isError,
    status,
  } = queryConfig.dataHook(managedQueryOptions)
  const [ttdTimeout, setTtdTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)
  // we keep track of the keys of our instances to enable the selection checks
  // in one useEffect and the table selection callback functions
  const dataRowKeys = data?.results.map(record => record.key)

  // table states
  // for showing and hiding columns we need to work with the column indices
  // as working on the columns directly changes their order when hiding and showing again
  // Use a ref to track if this is the first render, so we only initialize once
  const isFirstRender = React.useRef(true)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  let initializedFromStorage = false
  const [visibleColumnIndices, setVisibleColumnIndices] = useState<string[]>(() => {
    const allColumnKeys = columnsConfig.allColumns.map(clm => clm.key as string)

    if (columnVisibilityStorageKey && typeof window !== 'undefined') {
      try {
        const storedSelection = window.localStorage.getItem(columnVisibilityStorageKey)
        if (storedSelection) {
          const parsedSelection = JSON.parse(storedSelection)
          if (Array.isArray(parsedSelection)) {
            const sanitizedSelection = parsedSelection.filter((idx: unknown) =>
              typeof idx === 'string' && allColumnKeys.includes(idx)
            )
            if (sanitizedSelection.length > 0) {
              initializedFromStorage = true
              return sanitizedSelection
            }
          }
        }
      } catch (error) {
        log.warn('Failed to read column selection from storage', error)
      }
    }

    return columnsConfig.initialVisibleColumnIndices ?? allColumnKeys
  })
  
  // Only update visibleColumnIndices if columnsConfig.initialVisibleColumnIndices changes
  // and this is not the first render, but only if the user hasn't manually changed columns
  // We use a ref to track if columns have been manually changed
  const columnsManuallyChanged = React.useRef(initializedFromStorage)
  
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    
    // Only sync with initialVisibleColumnIndices if columns haven't been manually changed
    // and if initialVisibleColumnIndices is provided
    if (!columnsManuallyChanged.current && columnsConfig.initialVisibleColumnIndices) {
      // Check if any columns were removed from allColumns
      const currentColumnKeys = columnsConfig.allColumns.map(clm => clm.key as string)
      const removedColumns = visibleColumnIndices.filter(idx => !currentColumnKeys.includes(idx))
      
      // If columns were removed, update the visible indices
      if (removedColumns.length > 0) {
        setVisibleColumnIndices(prev => prev.filter(idx => currentColumnKeys.includes(idx)))
      }
    }
  }, [columnsConfig.allColumns, columnsConfig.initialVisibleColumnIndices])

  useEffect(() => {
    if (!columnVisibilityStorageKey || typeof window === 'undefined') return
    try {
      window.localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(visibleColumnIndices))
    } catch (error) {
      log.warn('Failed to persist column selection', error)
    }
  }, [columnVisibilityStorageKey, visibleColumnIndices])
  const visibleColumns = columnsConfig.allColumns.filter(
    clm => visibleColumnIndices.includes(clm.key as string)
  )
  // internally we keep track of the selected row keys in addition to the selected
  // rows, as keys make checking if a certain row is selected a lot easier.
  const selectedRowKeys = selection?.state.map(record => record.key)
  const selectedRecord = selection?.state?.[0]
  const selectionLength = selection?.state.length ?? 0
  const sortedColumnKey = resolveDataIndex(managedQueryOptions.sort?.property)
  const hasActiveFilters = Boolean(
    managedQueryOptions.filters
    && Object.values(managedQueryOptions.filters).some(value => {
      if (Array.isArray(value)) return value.length > 0
      return value !== undefined && value !== null && value !== ''
    })
  )
  const columnsWithSortOrder: SmartTableColumnType<TEntity>[] = visibleColumns.map(clm => {
    const columnKey = resolveDataIndex(clm.dataIndex)
    const sortOrder: SortOrder | undefined =
      sortedColumnKey && columnKey === sortedColumnKey
        ? managedQueryOptions.sort?.order === 'ASC'
          ? 'ascend'
          : managedQueryOptions.sort?.order === 'DESC'
            ? 'descend'
            : undefined
        : undefined

    return {
      ...clm,
      sortOrder,
    }
  })
  const columnsWithSticky: SmartTableColumnType<TEntity>[] = React.useMemo(
    () => {
      if (!hasHorizontalOverflow || columnsWithSortOrder.length === 0) return columnsWithSortOrder

      return columnsWithSortOrder.map((column, index) => {
        const isFirstColumn = index === 0
        const isLastColumn = index === columnsWithSortOrder.length - 1
        if ((!isFirstColumn && !isLastColumn) || column.fixed) return column

        return {
          ...column,
          fixed: isFirstColumn ? 'left' : 'right',
        }
      })
    },
    [columnsWithSortOrder, hasHorizontalOverflow]
  )

  // filter states
  const [filterDrawerFormState, setFilterDrawerFormState] = useState<GenericFilterType>({})
  useEffect(() => {
    if (!openFilterDrawer) return
    setFilterDrawerFormState({ ...(managedQueryOptions.filters ?? {}) })
  }, [openFilterDrawer, managedQueryOptions.filters])

  // table options states
  const [columnSelectionMenuAnchorElem, setColumnSelectionMenuAnchorElem] = useState<null | HTMLElement>(null)
  const [columnSelectionMenuPosition, setColumnSelectionMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const openColumnSelectionMenu = Boolean(columnSelectionMenuAnchorElem || columnSelectionMenuPosition)

  // context menu states
  const [contextMenuPosition, setContextMenuPosition] = useState<null | {mouseX: number, mouseY: number}>(null)
  const openContextMenu = Boolean(contextMenuPosition)
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------------- Effects start ---------------------------- */
  useEffect(() => {
    if(typeof data?.results !== 'undefined' && selection) {
      // the table data just updated -> check if there are any elements in the
      // selection state that are no longer visible. If so -> remove those elements

      // if there are currently no selected rows -> hide warning and do nothing
      if(selectedRowKeys!.length === 0) {
        setShowActiveSelectionWarning(false)
        return
      }
      // ... else check if there are selected rows that are no longer visible
      if(selectedRowKeys!.some(key => !dataRowKeys!.includes(key))) {
        // there is at least one selected row that is no longer visible -> show warning
        setShowActiveSelectionWarning(true)
      } else {
        // all selected rows are still visible -> hide the warning
        setShowActiveSelectionWarning(false)
      }
    }
  }, [data?.results, selection?.state])

  useEffect(() => {
    if (!selection) return
    if (selectionLength === 0 && openInfoPanel) {
      setOpenInfoPanel(false)
    }
  }, [selectionLength, openInfoPanel])

  const calcTableLayout = useCallback(() => {
    if (typeof window === 'undefined' || !tableContainerRef.current) return
    const rect = tableContainerRef.current.getBoundingClientRect()
    const bottomPadding = 32 // space for surrounding layout
    const reservedForTableChrome = 160 // header + pagination + margins
    const minHeight = 240
    const available = window.innerHeight - rect.top - bottomPadding - reservedForTableChrome
    setTableScrollY(Math.max(available, minHeight))

    const scrollContainer =
      tableContainerRef.current.querySelector('.ant-table-body')
      ?? tableContainerRef.current.querySelector('.ant-table-content')

    if (scrollContainer instanceof HTMLElement) {
      setHasHorizontalOverflow(scrollContainer.scrollWidth > scrollContainer.clientWidth)
    } else {
      setHasHorizontalOverflow(false)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    calcTableLayout()
    window.addEventListener('resize', calcTableLayout)
    window.addEventListener('scroll', calcTableLayout, { passive: true })
    return () => {
      window.removeEventListener('resize', calcTableLayout)
      window.removeEventListener('scroll', calcTableLayout)
    }
  }, [calcTableLayout])

  useEffect(() => {
    const timeout = setTimeout(() => calcTableLayout(), 0)
    return () => clearTimeout(timeout)
  }, [openInfoPanel, selectionLength, visibleColumns, data?.results, calcTableLayout])
  /* ------------------------------ Effects end ----------------------------- */

  /* ------------------------ Utility functions start ----------------------- */
  /**
   * Returns true if the given column is currently listed in the visibleColumns
   * and therefore visible in the table, otherwise false.
   * @param column SmartTableColumnType<TEntity> - The column to check for visibility.
   */
  function isColumnVisible(column: SmartTableColumnType<TEntity>): boolean {
    return visibleColumnIndices.some(
      columnKey => columnKey === column.key
    )
  }

  /**
   * Returns a human-readable label given the property of an entity / the dataIndex
   * of a column, or the filterKey if specified.
   * @param property - Property of an entity / dataIndex of a column / filterKey.
   * @returns string | undefined - Returns undefined if unable to resolve a humand readable name.
   */
  function getHumandReadablePropertyName(property: string): string | undefined {
    // First try to match by filterKey (for filters that use a different key than dataIndex)
    const columnByFilterKey = columnsConfig.allColumns
      .find(column => column.filter?.filterKey === property)
    if (columnByFilterKey && typeof columnByFilterKey.title === 'string') {
      return columnByFilterKey.title
    }
    // Fall back to matching by dataIndex
    const columnByDataIndex = columnsConfig.allColumns
      .find(column => column.dataIndex === property)
    if (columnByDataIndex && typeof columnByDataIndex.title === 'string') {
      return columnByDataIndex.title
    }
    // if the title is not a string, we have no way of safely determining a human-
    // readable name
    return undefined

  }

  /**
   * Formats a date value for display in filter chips.
   * Handles Date objects, ISO date strings (YYYY-MM-DD), and other strings.
   * @param value - The date value to format
   * @returns Formatted date string
   */
  function formatDateForChip(value: unknown): string {
    if (value instanceof Date) {
      return value.toLocaleDateString()
    }
    
    if (typeof value === 'string') {
      // Check if it's an ISO date string (YYYY-MM-DD)
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (isoDateRegex.test(value)) {
        try {
          const date = new Date(value + 'T00:00:00') // Add time to avoid timezone issues
          if (!isNaN(date.getTime())) {
            // Format as DD.MM.YYYY (German format)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}.${month}.${year}`
          }
        } catch (e) {
          // If parsing fails, return original string
        }
      }
      return value
    }
    
    return String(value)
  }

  function resolveDataIndex(dataIndex: unknown): string | undefined {
    if (Array.isArray(dataIndex)) {
      return dataIndex.join('.')
    }
    if (dataIndex === null || typeof dataIndex === 'undefined') return undefined
    return String(dataIndex)
  }

  type ExtractedField = {
    property: string
    value: unknown
    label: string
  }

  function isEmptyFieldValue(value: unknown): boolean {
    if (value === undefined || value === null) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    return false
  }

  function extractFieldsFromRecord(record?: TEntity): ExtractedField[] {
    if (!record) return []

    return Object.entries(record)
      .filter(([property, value]) => !['self', 'key'].includes(property) && !isEmptyFieldValue(value))
      .map(([property, value]) => ({
        property,
        value,
        label: getHumandReadablePropertyName(property) ?? property,
      }))
  }

  function formatFieldValue(value: unknown): string | React.ReactNode {
    if (value === null || typeof value === 'undefined') return '-'
    if (React.isValidElement(value)) return value
    if (value instanceof Date) return value.toLocaleString()
    if (typeof value === 'string') {
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T[\d:.+-Z]*)?$/
      if (isoDateRegex.test(value)) {
        const parsedDate = new Date(value)
        if (!Number.isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleString()
        }
      }
      return value
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return '-'
      const formattedArray = value
        .map(entry => formatFieldValue(entry))
        .map(entry => {
          if (typeof entry === 'string') return entry
          if (React.isValidElement(entry)) return ''
          if (typeof entry === 'object') return JSON.stringify(entry)
          return String(entry)
        })
        .filter(entry => entry !== '')
      return formattedArray.length > 0 ? formattedArray.join(', ') : '-'
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return String(value)
      }
    }
    return String(value)
  }

  function groupFieldsIntoAccordions(fields: ExtractedField[]): Array<{title: string, fields: ExtractedField[]}> {
    if (fields.length === 0) return []
    if (fields.length <= 8) {
      return [{
        title: '',
        fields,
      }]
    }

    const accordionTitles = [
      t('common:content.label.general', { defaultValue: 'General' }),
      t('common:content.label.details', { defaultValue: 'Details' }),
      t('common:content.label.additionalInformation', { defaultValue: 'Additional Information' }),
      t('common:content.label.moreInformation', { defaultValue: 'More Information' }),
    ]

    const groups: Array<{title: string, fields: ExtractedField[]}> = []
    for (let i = 0; i < fields.length; i += 8) {
      const sectionIndex = i / 8
      const title = accordionTitles[sectionIndex]
        ?? `${t('common:content.label.details', { defaultValue: 'Details' })} ${sectionIndex + 1}`
      groups.push({
        title,
        fields: fields.slice(i, i + 8),
      })
    }
    return groups
  }
  /* ------------------------- Utility functions end ------------------------ */

  /* ----------------------- Callback functions start ----------------------- */

  // Table callbacks
  const handleRowSelection = (selectedRows: TEntity[]): void => {
    selection?.setState(selectedRows)
    selection?.onChange?.(selectedRows)
  }
  const handleRowClick = (record: TEntity, event: React.MouseEvent<any, MouseEvent>): void => {
    if (selection) {
      handleRowSelection([record])
      setOpenInfoPanel(true)
    }
    onRow?.onClick?.(record, event)
  }
  const handleRowDoubleClick = (record: TEntity, event: React.MouseEvent<any, MouseEvent>): void => {
    onRow?.onDoubleClick?.(event)
  }
  const handleRowRightClick = (record: TEntity, event: React.MouseEvent<any, MouseEvent>): void => {
    if(!contextMenuOptions) return
    event.preventDefault()
    setContextMenuPosition(
      {
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      }
    )
    // set the right-clicked record as selected for visual feedback
    handleRowSelection([record])
  }
  const handlePageChange = (page: number, pageSize: number): void => {
    setManagedQueryOptions(prev => ({...prev, page, pageSize}))
  }
  const handleDeSelectAll = (): void => {
    // if there is no selection config or no data, then there is nothing to deselect
    if(!selection || !data) return
    handleRowSelection([])
    // hide warning
    setShowActiveSelectionWarning(false)
  }
  const handleDeSelectNotVisible = (): void => {
    // if there is no selection config or no data, then there is nothing to deselect
    if(!selection || !data) return
    const dataRowKeys = data.results.map(record => record.key)
    const updatedSelectedRows = [...selection.state]
    selectedRowKeys!.forEach(key => {
      if(!dataRowKeys.includes(key)) {
        // the selected row is no longer visible -> de-select the row
        const i = updatedSelectedRows.findIndex(record => record.key === key)
        updatedSelectedRows.splice(i, 1)
      }
    })
    // finally write the update to state
    handleRowSelection(updatedSelectedRows)
    // hide warning
    setShowActiveSelectionWarning(false)
  }

  // Filter callbacks
  const handleApplyFilter = (filters: TFilters | undefined): void => {
    // setting the query options triggers the query and updates the data automatically
    setManagedQueryOptions(prev => ({
      ...prev,
      filters: filters as TFilters,
    }))
  }
  const handleRemoveSingleFilter = (filterProperty: string): void => {
    // ? debounce applying the updated filters to the query?
    // ? This would require a separate rendering state (e.g., activeFilters)
    setManagedQueryOptions(prev => {
      delete prev.filters?.[filterProperty]
      return {...prev}
    })
    setFilterDrawerFormState(prev => {
      delete prev[filterProperty]
      return {...prev}
    })
  }

  // Options callbacks (e.g. ColumnSelector)
  const handleColumnSelectionChange = (selectedClm: SmartTableColumnType<TEntity>): void => {
    const isCurrentlyVisible = isColumnVisible(selectedClm)
    columnsManuallyChanged.current = true // Mark that columns have been manually changed
    setVisibleColumnIndices(prev => {
      const updated = isCurrentlyVisible
        ? prev.filter(columnKey => columnKey !== selectedClm.key)
        : [...prev, selectedClm.key as string]
      onColumnVisibilityChange?.(updated)
      return updated
    })
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------ Render constants start ------------------------ */
  const loadingTable = (rows = 10): React.ReactNode => {
    const range: number[] = []
    for(let i=1; i<=rows;i++) range.push(i)
    return (
      <Box
        /* Wrapper element to counteract the 16px padding of the AntD Table component.*/
        display='block'
        margin='-16px'
      >
        <table>
          <thead style={{visibility: 'collapse'}}>
            <tr>
              {visibleColumns.map((clm: SmartTableColumnType<TEntity>, index: number) => (
                <th
                  // eslint-disable-next-line react/no-array-index-key
                  key={`loadingTableCol${index}`}
                  style={{
                    width: clm.width,
                    minWidth: clm.width,
                    padding: '16px'
                  }}
                >
                  {typeof clm.title === 'function'
                    ? clm.title({
                        // clm.title as function requires a context argument, but for
                        // the loading table we do not care about the values within the context.
                        sortOrder: undefined,
                        sortColumn: undefined,
                        sortColumns: undefined,
                        filters: undefined,
                      })
                    : clm.title
                  }
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
          {range.map(row => {
            return (
              <tr key={row}>
                {visibleColumns.map((clm: SmartTableColumnType<TEntity>, col: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={`row${row}col${col}`} style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', }}>
                    <Skeleton variant='rounded' animation='wave' height='1.4rem'/>
                  </td>
                ))}
              </tr>
            )
          })}
          </tbody>
        </table>
      </Box>
    )
  }

  const activeSelectionWarning = (
    <Box
      id='activeSelectionWarning'
      bgcolor='#fff4e5'
      color='#663c00'
      borderRadius={2}
      display='flex'
      justifyContent='space-between'
    >
      <Box
        id='activeSelectionWarningContent'
        padding='8px 16px'
        display='flex'
        alignItems='center'
      >
        <WarningAmberRounded color='warning'/>
        <Typography
          variant='body2'
          color='inherit'
          ml='8px'
        >
          There are selected rows, currently not visible
        </Typography>
      </Box>
      <Stack direction='row' spacing={1} p='8px'>
        <Button
          variant='outlined'
          color='inherit'
          size='small'
          onClick={handleDeSelectAll}
        >
          Deselect all
        </Button>
        <Button
          variant='outlined'
          color='inherit'
          size='small'
          onClick={handleDeSelectNotVisible}
        >
          Deselect not visible
        </Button>
      </Stack>
    </Box>
  )

  const smartTableFilterChips = (
    <Stack
      id='activeFilterContainer'
      direction='row'
      spacing={1}
      flexGrow={1}
      flexWrap='nowrap'
      overflow='auto'
      minHeight='2rem'
      sx={{ minWidth: 0, flexShrink: 1 }}
    >
      {managedQueryOptions.filters && Object.entries(managedQueryOptions.filters).map(
        ([property ,value], index: number) => {
          const filterTitle = property === 'q'
            ? t('common:content.label.search')
            : getHumandReadablePropertyName(property)
          const formattedValue = formatDateForChip(value)
          return (
            <Chip
              id={`chip-${property === 'q' ? 'search-query' : property}`}
              key={`activeFilter${index}`}
              label={`${filterTitle}: ${formattedValue}`}
              onDelete={() => handleRemoveSingleFilter(property)}
            />
          )
        }
      )}
    </Stack>
  )

  const extractedFields = extractFieldsFromRecord(selectedRecord)
  const fieldGroups = groupFieldsIntoAccordions(extractedFields)
  const infoPanelLabel = t('common:content.label.details', { defaultValue: 'Details' })

  const renderInfoFields = (fieldsToRender: ExtractedField[]): React.ReactNode => (
    <Grid container spacing={2} sx={{ padding: '5px' }}>
      {fieldsToRender.map(field => (
        <Grid item xs={12} key={`infoPanel${field.property}`}>
          <InfoField
            fullWidth
            label={field.label}
            value={formatFieldValue(field.value)}
          />
        </Grid>
      ))}
    </Grid>
  )

  const smartTableInfoPanel = selectedRecord && openInfoPanel ? (
    <InfoPanel
      open={openInfoPanel}
      setOpen={setOpenInfoPanel}
      allowEdit={false}
      label={infoPanelLabel}
      disabled={false}
    >
      {fieldGroups.length > 1
        ? fieldGroups.map((group, index) => (
            <Accordion
              key={`smartTableInfoAccordion${index}`}
              defaultExpanded={index === 0}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`smartTable-info-panel-content-${index}`}
                id={`smartTable-info-panel-header-${index}`}
              >
                <Typography>{group.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderInfoFields(group.fields)}
              </AccordionDetails>
            </Accordion>
          ))
        : extractedFields.length > 0
          ? renderInfoFields(extractedFields)
          : (
            <Box px={2} py={1}>
              <Typography variant='body2'>
                {t('common:content.label.noData', { defaultValue: 'No data available' })}
              </Typography>
            </Box>
          )
      }
    </InfoPanel>
  ) : null

  const customInfoPanelContent = typeof customInfoPanel === 'function'
    ? customInfoPanel(openInfoPanel, setOpenInfoPanel)
    : customInfoPanel
  
  const renderedInfoPanel = customInfoPanelContent && selectedRecord && openInfoPanel ? (
    <InfoPanel
      open={openInfoPanel}
      setOpen={setOpenInfoPanel}
      allowEdit={false}
      label={infoPanelLabel}
      disabled={false}
    >
      {customInfoPanelContent}
    </InfoPanel>
  ) : smartTableInfoPanel

  const hasColumnSelector = !hideColumnSelector
  const availableTableOptionsCount =
    (onShare ? 1 : 0) +
    (tableOptions?.length ?? 0) +
    (hasColumnSelector ? 1 : 0)
  const hasRightControls = !hideFilters || availableTableOptionsCount > 0 || Boolean(onShare)
  const shouldBypassTableOptionsMenu = hasColumnSelector && availableTableOptionsCount === 1
  const handleTableOptionsPrimaryClick = shouldBypassTableOptionsMenu
    ? (buttonEl: HTMLButtonElement | null, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        if (buttonEl) {
          setColumnSelectionMenuAnchorElem(buttonEl)
        }
      }
    : undefined
  /* ------------------------- Render constants end ------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */
  if(isPlaceholderData && !isPendingForTooLong && ttdTimeout === null) {
    // we are currently waiting for new data. While the user waits, placeholder is
    // displayed (beeing optimisitc that the data loads fast enough, the user does
    // not notice it had to be fetched from the backend). If for some reason loading
    // the new data takes longer than expected, we need to display the loading state
    log.debug('Starting placeholder TTD timeout now ...')
    setTtdTimeout(setTimeout(() => {
      // this sets the isPendingForTooLong state to true, if the timeout is not
      // cleared beforehand.
      log.warn(
        placeholderDataTTD, 'ms TTD have passed and the new data is not ' +
        'available yet -> displaying loading state until data is available.'
      )
      setIsPendingForTooLong(true)
    }, placeholderDataTTD))
  }
  if(data && !isPlaceholderData && ttdTimeout !== null) {
    log.debug('Clearing TTD timeout and resetting all timeout related states')
    // reset the timeout mechanism states
    clearTimeout(ttdTimeout) // clearing the timeout is important! Otherwise, the
                             // timeout will be executed, even though it is no
                             // longer necessary.
    setTtdTimeout(null)
    setIsPendingForTooLong(false)
  }
  /* ------------------------ Pre render actions end ------------------------ */


  log.debug(
    'query states -> ', { data, isPlaceholderData, isPending, isPendingForTooLong },
  )

  const shouldHidePlaceholderData = isPlaceholderData && hasActiveFilters


  /* ------------------------------------------------------------------------ */
  /*                             Render Component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        position: 'relative',
        overflowX: 'hidden',
        '& .smart-table-scrollable .ant-table-container .ant-table-body, & .smart-table-scrollable .ant-table-container .ant-table-content': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#eaeaea transparent',
        },
        '& .smart-table-scrollable .ant-table-container .ant-table-body::-webkit-scrollbar, & .smart-table-scrollable .ant-table-container .ant-table-content::-webkit-scrollbar': {
          height: '8px',
          width: '8px',
        },
        '& .smart-table-scrollable .ant-table-container .ant-table-body::-webkit-scrollbar-thumb, & .smart-table-scrollable .ant-table-container .ant-table-content::-webkit-scrollbar-thumb': {
          backgroundColor: '#d9d9d9',
          borderRadius: '8px',
        },
        '& .smart-table-scrollable .ant-table-container .ant-table-body::-webkit-scrollbar-track, & .smart-table-scrollable .ant-table-container .ant-table-content::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '& .smart-table-scrollable .ant-table-cell-fix-left, & .smart-table-scrollable .ant-table-cell-fix-right': {
          backgroundColor: 'background.paper',
        },
        '& .smart-table-scrollable .ant-table-cell-fix-left-last::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '6px',
          boxShadow: 'inset -6px 0 6px -6px rgba(0, 0, 0, 0.12)',
          transform: 'translateX(50%)',
        },
        '& .smart-table-scrollable .ant-table-cell-fix-right-first::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '6px',
          boxShadow: 'inset 6px 0 6px -6px rgba(0, 0, 0, 0.12)',
          transform: 'translateX(-50%)',
        },
        '& .smart-table-scrollable': {
          width: '100%',
          maxWidth: '100%',
        },
        '& .smart-table-scrollable .ant-table-container': {
          width: '100%',
          maxWidth: '100%',
        },
        '& .smart-table-scrollable .ant-table-expanded-row > td.ant-table-cell': {
          padding: 0,
        },
        ...sx,
      }}
    >
      <Box>
        {showActiveSelectionWarning &&
          <Box display='flex' justifyContent='flex-end'>
            {activeSelectionWarning}
          </Box>
        }
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          {!hideFilters && smartTableFilterChips}
          {typeof actions !== 'undefined' && (
            <Box sx={{ marginLeft: hideFilters ? 'auto' : undefined }}>
              {actions}
            </Box>
          )}
          {hasRightControls && (
            <Stack 
              direction='row' 
              spacing={1} 
              alignItems='center' 
              pl={1}
              sx={{ flexShrink: 0 }}
            >
              {!hideFilters && (
                <IconButton
                  id='smartTableFilterButton'
                  aria-controls='filter-options-drawer'
                  onClick={() => setOpenFilterDrawer(true)}
                >
                  <FilterAltRounded />
                </IconButton>
              )}
              {availableTableOptionsCount > 0 && (
                <TableOptions
                  open={openMoreOptionsMenu}
                  setOpen={setOpenMoreOptionsMenu}
                  onPrimaryClick={handleTableOptionsPrimaryClick}
                >
                  {onShare && (
                    <MenuItem
                      key={'tableOptionShare'}
                      onClick={() => {
                        setOpenMoreOptionsMenu(false)
                        onShare()
                      }}
                      disabled={isPending || isError}
                    >
                      <ListItemIcon><ShareRounded/></ListItemIcon>
                      <ListItemText>{t('sharepoint:interaction.button.createSharepoint')}</ListItemText>
                    </MenuItem>
                  )}
                  {tableOptions && tableOptions.map((option, index) => {
                    return (
                      <MenuItem
                        key={`tableOption${index}`}
                        id={option.id}
                        data-cy={option.id}
                        onClick={event => {
                          setOpenMoreOptionsMenu(false)
                          option.onClick(event)
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    )
                  })}
                  {hasColumnSelector && (
                    <MenuItem
                      key={'tableOptionColumnSelectionMenu'}
                      id="columnSelector"
                      data-testid="column-selector-menu-item"
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        // Store the menu item's position before it gets removed from DOM
                        const rect = e.currentTarget.getBoundingClientRect()
                        setColumnSelectionMenuPosition({
                          top: rect.bottom,
                          left: rect.left
                        })
                        // Close the more options menu immediately
                        setOpenMoreOptionsMenu(false)
                      }}
                    >
                      {t('common:content.label.columnSelector')}
                    </MenuItem>
                  )}
                </TableOptions>
              )}
              {onShare && (
                <IconButton
                  disabled={isPending || isError}
                >

                </IconButton>
              )}
            </Stack>
          )}
        </Box>
        <Box
          ref={tableContainerRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            maxHeight: tableScrollY > 0 ? `${tableScrollY + 160}px` : 'none',
            overflow: 'hidden',
          }}
        >
          <Table
            className='smart-table-scrollable'
            id={tableId}
            style={{ width: '100%', minWidth: '100%' }}
            locale={{
              // removing sorting tooltips
              triggerAsc: '',
              triggerDesc: '',
              cancelSort: '',
              emptyText: (
                (!data && isPending) // loading data for the first time
                || (data && isPlaceholderData && (isPendingForTooLong || shouldHidePlaceholderData)) // waiting for new data (e.g. after sorting)
              )
                ? loadingTable(managedQueryOptions.pageSize ?? defaultTablePageSize)
                : t('common:content.label.noData')
            }}
            /* Keep the active sort mirrored on the columns so AntD renders the arrows and highlight */
            columns={columnsWithSticky}
            dataSource={
              !(isPendingForTooLong || shouldHidePlaceholderData)
              ? data?.results
              : undefined // remove placeholder data if fetching the new data takes
                          // longer than expected. We do this to give the user the
                          // feedback about the loading state
            }
            rowSelection={selection && {
              selectedRowKeys: selectedRowKeys,
              onChange: (_, selectedRows, __) => handleRowSelection(selectedRows),
              ...(hasHorizontalOverflow ? { fixed: 'left' as const } : {}),
            }}
            onRow={record => ({
                onClick: clickEvent => handleRowClick(record, clickEvent),
                onDoubleClick: dblClickEvent => handleRowDoubleClick(record, dblClickEvent),
                onContextMenu: rightClickEvent => contextMenuOptions && handleRowRightClick(record, rightClickEvent)
              })
            }
            rowClassName={rowClassName}
          pagination={{
            current: managedQueryOptions.page ?? 1,
            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            pageSizeOptions: availableTablePageSizes,
            pageSize: managedQueryOptions.pageSize ?? defaultTablePageSize,
            /* eslint-enable @typescript-eslint/no-unsafe-assignment */
            total: data?.count,
            showQuickJumper: false,
            onChange: handlePageChange
          }}
          onChange={(_, __, sorter) => {
              // This callback handles the sorting feature of the AntD Table.

              // We need to check that sorter is an object containing the selected
              // sorting information. This is necessary as the AntD Table also supports
              // sorting by multiple columns and therefore can return an array of sorter
              // objects.
              if(Array.isArray(sorter) || typeof sorter !== 'object') return

              // TypeScript does not like the sorter object -> typecast to any
              const looseSorter = sorter as any
              const sortOrder = looseSorter.order === 'ascend'
                ? 'ASC'
                : looseSorter.order === 'descend'
                  ? 'DESC'
                  : undefined
              const sortProperty = resolveDataIndex(looseSorter.field ?? looseSorter.column?.dataIndex)

              // update sorting arguments
              setManagedQueryOptions(prev => {
                const next = { ...prev }
                if (sortOrder && sortProperty) {
                  next.sort = {
                    order: sortOrder,
                    property: sortProperty as keyof TEntity,
                  }
                } else {
                  delete next.sort
                }
                return next
              })
            }}
            tableLayout='auto'
            scroll={{
              x: 'max-content',
              y: tableScrollY || undefined,
            }}
            sticky
            expandable={expandable && {
              expandedRowRender: expandable.expandedRowRender,
              rowExpandable: expandable.rowExpandable,
            }}
          />
        </Box>

        {contextMenuOptions && (
          <Menu
            open={openContextMenu}
            onClose={() => setContextMenuPosition(null)}
            anchorReference='anchorPosition'
            anchorPosition={
              contextMenuPosition !== null
                ? {top: contextMenuPosition.mouseY, left: contextMenuPosition.mouseX}
                : undefined
            }
          >
            {contextMenuOptions.map((option, index) => {
              return (
                <MenuItem
                  key={`contextMenuOption${index}`}
                  onClick={e => {
                    option.onClick(selection!.state[0], e)

                    // close context menu
                    setContextMenuPosition(null)
                  }}
                  dense
                  sx={{minWidth: '250px'}}
                >
                  {option.icon && (
                    <ListItemIcon>
                      {option.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText>
                    {option.label}
                  </ListItemText>
                </MenuItem>
              )
            })}
          </Menu>
        )}


        {hasColumnSelector && (
          <Menu
            id='columnSelectionMenu'
            data-testid="column-selection-menu"
            open={openColumnSelectionMenu}
            anchorEl={columnSelectionMenuAnchorElem}
            anchorReference={columnSelectionMenuPosition ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={columnSelectionMenuPosition ? columnSelectionMenuPosition : undefined}
            onClose={() => {
              setColumnSelectionMenuAnchorElem(null)
              setColumnSelectionMenuPosition(null)
            }}

            /* Transformation */
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
          >
            {columnsConfig.allColumns.map((clm, index) => {
              if(clm.key !== '' && clm.key !== undefined && typeof clm.title === 'string' && clm.title !== '') {
                return (
                  <MenuItem
                    key={`columnSelector${index}`}
                    onClick={e => {
                      // handle the click event here instead of on the checkbox
                      // to make the whole MenuItem clickable instead of just the
                      // FormControlLabel
                      e.stopPropagation()
                      e.preventDefault()
                      log.debug('race')
                      handleColumnSelectionChange(clm)
                    }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            name={clm.key as string}
                            checked={isColumnVisible(clm)}
                            onChange={e => {
                              // Allow Cypress `.check()` to toggle columns without
                              // relying on the MenuItem click handler.
                              e.stopPropagation()
                              e.preventDefault()
                              handleColumnSelectionChange(clm)
                            }}
                            inputProps={{
                              'data-testid': `column-selector-checkbox-${clm.key}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>}
                          />
                        }
                      label={clm.title}
                    />
                  </MenuItem>
                )
              }
              return null
            })}
          </Menu>
        )}
        {!hideFilters && (
          <TableFilterDrawer
            open={openFilterDrawer}
            setOpen={setOpenFilterDrawer}
            formState={{
              state: filterDrawerFormState,
              setState: setFilterDrawerFormState
            }}
            tableColumns={columnsConfig.allColumns}
            onApplyFilter={handleApplyFilter}
            searchLabel={searchLabel}
          />
        )}
      </Box>
      {renderedInfoPanel}
    </Box>
  )
}
